import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Bug, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import type { CreateBugReport } from '@pm/shared';
import { ApiError, api } from '@/lib/api';
import { pmContext } from '@/lib/client-errors';
import { Button } from '@/ui/Button';
import { Field, TextArea, TextInput } from '@/ui/Field';
import { FileUpload, type UploadedFile } from '@/ui/FileUpload';
import { Modal } from '@/ui/Modal';

/** What a screenshot may be - the uploads endpoint also takes documents. */
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/**
 * A floating "Report bug" button on every PM page. The report carries the
 * page it came from and the board or workspace shown there; the API keeps
 * those only when the reporter can open them.
 */
export function ReportBugButton() {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<UploadedFile | null>(null);
  const [notImage, setNotImage] = useState(false);

  const send = useMutation({
    mutationFn: (body: CreateBugReport) => api.post<{ id: string }>('/pm/bug-reports', body),
  });

  const close = () => {
    setOpen(false);
    if (send.isSuccess) {
      setTitle('');
      setDescription('');
      setScreenshot(null);
      send.reset();
    }
  };

  const submit = () => {
    const { boardId, workspaceId } = pmContext(location.pathname);
    send.mutate({
      title,
      description,
      url: `${location.pathname}${location.search}`,
      boardId,
      workspaceId,
      screenshotId: screenshot?.id,
    });
  };

  const fields = send.error instanceof ApiError ? send.error.fieldMap : {};
  const general = send.error instanceof ApiError && !send.error.fields?.length ? send.error.message : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('bugReport.button')}
        className="fixed end-5 bottom-5 z-30 flex h-10 items-center gap-2 rounded-full bg-primary px-4 text-sm font-medium text-white shadow-[var(--shadow-raised)] transition-colors hover:bg-primary-soft"
      >
        <Bug className="size-4" aria-hidden />
        <span className="hidden sm:inline">{t('bugReport.button')}</span>
      </button>

      <Modal
        open={open} onClose={close} title={t('bugReport.title')}
        footer={send.isSuccess ? (
          <Button onClick={close}>{t('common.close')}</Button>
        ) : (<>
          <Button variant="ghost" onClick={close}>{t('common.cancel')}</Button>
          <Button loading={send.isPending} disabled={!title.trim() || !description.trim()} onClick={submit}>
            {t('bugReport.send')}
          </Button>
        </>)}
      >
        {send.isSuccess ? (
          <p role="status" className="flex items-start gap-2 text-sm text-ink">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
            {t('bugReport.sent')}
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            <Field label={t('bugReport.titleField')} error={fields.title} required>
              <TextInput value={title} maxLength={255} autoFocus error={Boolean(fields.title)}
                         onChange={(e) => setTitle(e.target.value)} />
            </Field>
            <Field label={t('bugReport.description')} error={fields.description}
                   hint={t('bugReport.descriptionHint')} required>
              <TextArea value={description} maxLength={5000} rows={5} error={Boolean(fields.description)}
                        onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <Field label={t('bugReport.screenshot')}
                   error={notImage ? t('bugReport.notAnImage') : fields.screenshotId}
                   hint={t('bugReport.screenshotHint')}>
              <FileUpload
                value={screenshot?.url ?? ''}
                name={screenshot?.name}
                accept={IMAGE_TYPES.join(',')}
                onChange={(file) => {
                  const image = !file || IMAGE_TYPES.includes(file.mimeType);
                  setNotImage(!image);
                  setScreenshot(image ? file : null);
                }}
              />
            </Field>
            {general && (
              <p role="alert" className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-sm text-danger">
                {general}
              </p>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
